import { natif, plateforme } from './plateforme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8502/api';

/* ── Applications des stores : notifications natives via Firebase ─────────────
 * Le module n'est chargé que dans l'application mobile : le site n'en télécharge rien. */
const CLE_JETON = 'equipote_jeton_natif';
let permNatif = 'prompt';
const messagerie = async () => (await import('@capacitor-firebase/messaging')).FirebaseMessaging;
const envoyerJeton = (chemin, token) => fetch(`${API_URL}/push/${chemin}`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('handball_token')}` },
  body: JSON.stringify({ token, plateforme })
});

/** Au démarrage de l'application mobile : état de la permission, et suivi des changements de jeton. */
export async function initNatif() {
  if (!natif) return;
  try {
    const M = await messagerie();
    permNatif = (await M.checkPermissions()).receive;
    M.addListener('tokenReceived', ({ token }) => {
      if (permNatif !== 'granted' || !localStorage.getItem('handball_token')) return;
      localStorage.setItem(CLE_JETON, token); envoyerJeton('natif', token).catch(() => {});
    });
  } catch { /* Firebase indisponible : l'application fonctionne, sans notifications */ }
}

/** Toucher une notification : l'application s'ouvre sur la bonne page (?messages, ?convocations…). */
export async function ecouterNotificationsNatives(onUrl) {
  if (!natif) return;
  try {
    const M = await messagerie();
    M.addListener('notificationActionPerformed', e => { const url = e?.notification?.data?.url; if (url) onUrl(url); });
  } catch {}
}

/** Déconnexion : ce téléphone ne doit plus recevoir les notifications du compte qui s'en va. */
export async function oublierAppareilNatif() {
  if (!natif) return;
  const token = localStorage.getItem(CLE_JETON);
  if (token) await envoyerJeton('natif/retirer', token).catch(() => {});
  localStorage.removeItem(CLE_JETON);
}

export function pushSupporte() {
  if (natif) return true;
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function estInstallee() {
  if (natif) return true;
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

export function permission() {
  if (natif) return permNatif === 'granted' ? 'granted' : permNatif === 'denied' ? 'denied' : 'default';
  return pushSupporte() ? Notification.permission : 'unsupported';
}

function b64ToUint8(base64) {
  const pad = '='.repeat((4 - base64.length % 4) % 4);
  const b64 = (base64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(b64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

function nomAppareil() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Appareil';
}

export async function enregistrerSW() {
  if (!('serviceWorker' in navigator)) return null;
  try { return await navigator.serviceWorker.register('/sw.js'); }
  catch { return null; }
}

export async function activerNotifications() {
  if (natif) {
    if (!localStorage.getItem('handball_token')) throw new Error('Connecte-toi d\'abord');
    const M = await messagerie();
    permNatif = (await M.requestPermissions()).receive;
    if (permNatif !== 'granted') throw new Error('Tu as refusé les notifications');
    const { token } = await M.getToken();
    const res = await envoyerJeton('natif', token);
    if (!res.ok) throw new Error('Enregistrement impossible');
    localStorage.setItem(CLE_JETON, token);
    return true;
  }
  if (!pushSupporte()) throw new Error('Notifications non supportées sur cet appareil');

  const token = localStorage.getItem('handball_token');
  if (!token) throw new Error('Connecte-toi d\'abord');

  const r = await fetch(`${API_URL}/push/cle`);
  const { cle, actif } = await r.json();
  if (!actif || !cle) throw new Error('Notifications non configurées sur le serveur');

  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Tu as refusé les notifications');

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64ToUint8(cle)
    });
  }

  const j = sub.toJSON();
  const res = await fetch(`${API_URL}/push/abonner`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ endpoint: j.endpoint, keys: j.keys, appareil: nomAppareil() })
  });
  if (!res.ok) throw new Error('Enregistrement impossible');
  return true;
}

export async function desactiverNotifications() {
  if (natif) {
    await oublierAppareilNatif();
    try { await (await messagerie()).deleteToken(); } catch {}
    return true;
  }
  const token = localStorage.getItem('handball_token');
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;
  await fetch(`${API_URL}/push/desabonner`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ endpoint: sub.endpoint })
  }).catch(()=>{});
  await sub.unsubscribe();
  return true;
}

export async function estAbonne() {
  if (natif) return permNatif === 'granted' && !!localStorage.getItem(CLE_JETON);
  if (!pushSupporte() || Notification.permission !== 'granted') return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    return !!(await reg.pushManager.getSubscription());
  } catch { return false; }
}

export async function testerNotification() {
  const token = localStorage.getItem('handball_token');
  const res = await fetch(`${API_URL}/push/test`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.error || 'Échec'); }
  return true;
}
